/**
 * labBackground.js
 *
 * ─── LAB → background state bridge ───────────────────────────────────────────
 *
 * A tiny module-level mutable store that lets LAB UI interactions influence the
 * autonomous particle field WITHOUT per-frame React state.
 *
 *   Demos writes  → setters below, called from its existing event handlers
 *                   (drag start/stop, active-item change, filter change). These
 *                   are plain function calls — no setState, so no re-render.
 *   Sketch reads  → getLabState() once per animation frame and converts the
 *                   values into a scalar shader input (uLabFlowStrength).
 *
 * Deliberately NOT a React hook / pub-sub-with-render (unlike useHomeSection in
 * homeSection.js): the only consumer (Sketch) polls imperatively, which is
 * exactly how LAB interactions are kept off the React per-frame path. getLabState
 * returns the SAME object every call (stable reference) so there is no per-frame
 * allocation; treat it as read-only.
 *
 * Pulses are counter-based (pulseSeq), not timestamps — Sketch remembers the last
 * pulseSeq and injects energy when it changes (no Date.now needed).
 *
 * Phase 6 adds a steady per-item focus profile (focusDiffusion): the active item's most
 * distinctive category biases the field's curl-flow energy. Sketch eases toward it and
 * applies it only when uLabBlend > 0, so the default field and reduced motion stay calm.
 *
 * Phase 7 adds filterSpread: the filtered set's relative size (count / total). Sketch eases
 * toward it as a small steady flow lean (small sets = slightly calmer/tighter field); the
 * filter-change reformation impulse itself rides the existing kind-2 pulse (pulseLab(2)).
 * ──────────────────────────────────────────────────────────────────────────── */

const labState = {
    active: false,      // LAB is the active section
    dragging: false,    // a LAB drag is in progress
    activeIndex: 0,     // current LAB item index
    filterKey: null,    // current LAB filter key
    pulseSeq: 0,        // one-shot event counter (bumped on active/filter change)
    pulseKind: 0,       // magnitude hint for the last pulse: 1 = active change, 2 = filter change
    focusDiffusion: 0.5,// Phase 6: active item's category lean — 0 = calm/structured, 1 = diffuse/unstable, 0.5 = neutral
    filterSpread: 1.0,  // Phase 7: filtered set's relative size (count / total) — 1 = full archive
    carouselOffset: 0,  // LabChamber virtualOffset (item-index float) — also drives the shimmer background's
                        // horizontal rotation (ShimmeringPrism polls this, second consumer besides the Sketch)
};

export function setLabActive(v) {
    labState.active = !!v;
    // Leaving LAB clears the in-progress drag so no stale drag survives the exit.
    if (!v) labState.dragging = false;
}

export function setLabDragging(v) {
    labState.dragging = !!v;
}

/** Publish the carousel's continuous virtualOffset (item-index float). Polled by ShimmeringPrism to rotate
 *  the background horizontally with the LAB drag/coast. NaN-guarded (the offset feeds a delta accumulator). */
export function setLabCarouselOffset(v) {
    if (typeof v === 'number' && !Number.isNaN(v)) labState.carouselOffset = v;
}

export function setLabActiveIndex(i) {
    labState.activeIndex = i | 0;
}

export function setLabFilter(k) {
    labState.filterKey = k;
}

/**
 * Fire a one-shot field reaction.
 * @param {number} kind  1 = active-item change (subtle), 2 = filter change (stronger).
 */
export function pulseLab(kind) {
    labState.pulseSeq = (labState.pulseSeq + 1) | 0;
    labState.pulseKind = kind;
}

/**
 * Set the active item's focus profile (Phase 6 — per-item / category shaping).
 * One scalar today: `diffusion` ∈ [0,1] (low = calm/structured, high = diffuse/unstable;
 * 0.5 = neutral). A TARGET only — Sketch eases toward it and applies it solely when
 * uLabBlend > 0, so it is inert in the default field and zeroed under reduced motion.
 * @param {{diffusion:number}} profile
 */
export function setLabFocusProfile(profile) {
    if (profile && typeof profile.diffusion === 'number') {
        labState.focusDiffusion = Math.max(0, Math.min(1, profile.diffusion));
    }
}

/**
 * Set the filtered set's relative size (Phase 7 — filter reformation).
 * `spread` ∈ [0,1] = filtered count / full archive count (1 = full set). A TARGET only —
 * Sketch eases toward it as a small steady flow lean; the one-shot reformation contraction
 * rides the existing kind-2 pulse (pulseLab(2)), not this value.
 * @param {number} spread
 */
export function setLabFilterSpread(spread) {
    if (typeof spread === 'number' && !Number.isNaN(spread)) {
        labState.filterSpread = Math.max(0, Math.min(1, spread));
    }
}

/** Stable read-only snapshot (same object every call — no per-frame allocation). */
export function getLabState() {
    return labState;
}
